import React, { useState } from 'react';
import { Modal, Button, Input, Select } from 'antd';
import styles from './update-stage.module.scss';
import { useSelector } from 'react-redux';
import { clearSelectedLeads, closeUpdateLead, setSelectedLead } from 'redux/spot/spot-actions';
import { RadioGroup } from 'components/pages/crm-spot/shared-components';
import { useNotification } from 'components/pages/crm-spot/layouts/crm-spot-layout';
import useArchivedReasons from 'components/pages/crm-spot/hooks/useArchivedReasons';
import { useToggle } from 'hooks/useToggle';
import { ArrowLeftOutlined } from '@ant-design/icons';

export const UpdateStage = ({ notesHook, assignments, userId }) => {
  const { isUpdateLeadOpen, selectedLead, selectedLeads, isAssignedMode } = useSelector((state) => state.spot);

  const [value, _setValue] = useState(selectedLead?.sub_status || selectedLead?.statu || 'new');
  const [hasSelection, toggleSelection] = useToggle(false);
  const setValue = (newValue) => {
    _setValue(newValue);
    !hasSelection && toggleSelection();
  };
  const backToSelection = () => {
    toggleSelection();
    setValue(null);
  };

  const [selectedOption, setOption] = useState(null);
  const selectedSchoolId =
    selectedLeads && selectedLeads.length > 0
      ? selectedLeads.every((lead) => lead.school_id === selectedLeads[0].school_id)
        ? selectedLeads[0].school_id
        : 0
      : selectedLead
      ? selectedLead.school_id
      : 0;

  const { reasons, loading } = useArchivedReasons(value === 'archived' ? selectedSchoolId : undefined);

  const { openNotification } = useNotification();

  const [note, setNote] = useState('');
  const { createNode } = notesHook;
  const handleNewNote = async () => {
    if (note.trim()) {
      await createNode({ leadId: selectedLead.id, note });
      setNote('');
    }
  };

  // handle lead update, dipatch notification and note creation
  const updateLeadStatus = async () => {
    try {
      const updatedAssign = await assignments.updateAssignment({
        user_id: userId,
        newValue: value,
        archivedReason: value === 'archived' ? selectedOption : null,
        acceptedYear: value === 'accepted' ? selectedOption : null,
      });
      // dispatch UI feedback
      if (updatedAssign) {
        openNotification({
          message: 'Status Updated',
          description: `Updated ${selectedLeads.length > 1 ? 'leads' : 'lead'} to ${value.toLowerCase()} successfully.`,
        });
      }
    } catch (error) {
      console.log('updateLeadStatus', error);
    }

    await handleNewNote();

    toggleSelection();
    setOption(null);
    _setValue(null);
    closeUpdateLead();
    clearSelectedLeads();
  };

  const initialStages = [
    {
      value: 'new',
      label: 'New Lead',
      description: 'Fresh lead that needs initial contact',
    },
    {
      value: 'attempting-contact',
      label: 'Attempting Contact',
      description: 'Actively trying to reach the family',
    },
    {
      value: 'working',
      label: 'Working',
      description: 'In active communication with the family',
    },
    ...(!isAssignedMode
      ? [
          {
            value: 'matched',
            label: 'Matched',
            description: "Family has been matched with a school but hasn't yet committed or applied",
          },
        ]
      : []),
  ];

  const declinedStages = [
    { value: 'archived', label: 'Archived', description: 'Lead is no longer active or has been declined' },
    ...(isAssignedMode
      ? [
          {
            value: 'waitlisted',
            label: 'Waitlist',
            description: 'Family/student is on the waitlist',
          },
        ]
      : []),
  ];

  const approvedStages = [
    {
      value: 'application-sent',
      label: 'Application Sent',
      description: 'Application has been sent to family',
    },
    ...(isAssignedMode
      ? [
          {
            value: 'application-received',
            label: 'Application Received',
            description: 'Application has been received from the family',
          },
          {
            value: 'accepted',
            label: 'Accepted',
            description: 'Family/student has been accepted',
          },
        ]
      : []),
  ];

  const selectedItem = [...initialStages, ...declinedStages, ...approvedStages].find((stage) => stage.value === value);

  const getNextSchoolYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 3 }, (_, i) => {
      const start = currentYear + i;
      return `${start}-${start + 1}`;
    });
  };

  return (
    <Modal
      title={
        selectedLeads.length > 0
          ? `Bulk Update Leads (${selectedLeads.length} selected)`
          : `Update Stage - ${selectedLead?.id}`
      }
      open={isUpdateLeadOpen}
      onCancel={() => {
        closeUpdateLead();
        setSelectedLead(null);
      }}
      footer={(_, { CancelBtn }) => (
        <>
          <CancelBtn />
          <Button type="primary" loading={assignments.loading} onClick={updateLeadStatus}>
            Update Lead
          </Button>
        </>
      )}>
      <div className={styles.stageContainer}>
        <p className={styles.stageLabel}>Select New Stage</p>
        {hasSelection ? (
          <>
            <Button onClick={backToSelection} icon={<ArrowLeftOutlined />}>
              Go back
            </Button>

            <RadioGroup
              options={[selectedItem]}
              value={value}
              setValue={setValue}
              style={{ padding: '1rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '.5rem' }}
            />
            {(value === 'archived' || value === 'accepted') &&
              (loading ? (
                <span>Loading...</span>
              ) : (
                <>
                  <label htmlFor="reason">
                    {value === 'archived' ? 'Select Archived Reason' : 'Select Accepted Year'}
                  </label>
                  <Select
                    id="reason"
                    style={{ width: '100%' }}
                    placeholder="Select an option"
                    options={
                      value === 'archived'
                        ? reasons.map((reason) => ({ label: reason.name, value: reason.id }))
                        : getNextSchoolYears().map((year) => ({ value: year, label: year }))
                    }
                    value={selectedOption}
                    onChange={setOption}
                  />
                </>
              ))}
            {selectedLeads.length === 0 && (
              <div style={{ marginTop: '1rem' }}>
                <label htmlFor="note">Add Note</label>
                <Input.TextArea
                  placeholder="Add a note about this stage update"
                  value={note}
                  onChange={({ target }) => setNote(target.value)}
                  rows={4}
                />
              </div>
            )}
          </>
        ) : (
          <>
            <RadioGroup
              options={initialStages}
              value={value || ''}
              setValue={setValue}
              style={{ padding: '1rem', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '.5rem' }}
            />
            <RadioGroup
              options={approvedStages}
              value={value || ''}
              setValue={setValue}
              style={{ padding: '1rem', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '.5rem' }}
            />
            <RadioGroup
              options={declinedStages}
              value={value || ''}
              setValue={setValue}
              style={{ padding: '1rem', background: '#e5e7eb', border: '1px solid #d1d5db', borderRadius: '.5rem' }}
            />
          </>
        )}
      </div>
    </Modal>
  );
};
